import {
  CurrencyEnum,
  Popover,
  Tooltip,
  copyTextToClipboard,
  useTranslation as useUiTranslation,
} from '@systemeio/ui'
import { AffiliateDashboardStatisticInterface } from 'modules/affiliate-dashboard/types/affiliate-dashboard-statistic-interface'
import { AffiliateStatisticCommonInterface } from 'modules/affiliate-program/affiliate/types/affiliate-interface'
import {
  getAffiliateSum,
  getAffiliateTotalSum,
} from 'modules/affiliate-program/utils/get-affiliate-sum'
import React, { useEffect, useState, type JSX } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { CopyIcon, QuestionMarkIcon } from '@systemeio/ui/icons'

interface AffiliateStatisticProps {
  affiliateStatistic?: AffiliateStatisticCommonInterface
  selectedCurrency?: CurrencyEnum
  isDashboard?: boolean
}

const AffiliateStatistic = ({
  affiliateStatistic,
  selectedCurrency,
  isDashboard = false,
}: AffiliateStatisticProps) => {
  const { t } = useLocoTranslation()
  const { t: tUi } = useUiTranslation()
  const { user } = useUser()

  const [mainCurrency, setMainCurrency] = useState<CurrencyEnum | undefined>()

 useEffect(() => {
    if (!affiliateStatistic) return

    const allCurrencies = Object.keys(affiliateStatistic.total) as CurrencyEnum[]
    if (selectedCurrency) {
      setMainCurrency(undefined)
    } else {
      setMainCurrency(
        allCurrencies[
          Object.values(affiliateStatistic.total).reduce(
                          (iMax, x, i, arr) => (typeof arr[iMax] !== 'undefined' &&  x > arr[iMax] ? i : iMax),
            0,
          )
        ],
      )
    }
  }, [selectedCurrency, affiliateStatistic])

  return (
    <div className={'flex flex-col gap-5'}>
      <div className={'bg-background-secondary flex w-full flex-col overflow-x-hidden rounded-lg'}>
        <span
          className={'text-text-primary bg-brand-primary-subtle px-6 py-4 text-[17px] font-bold'}
        >
          {t('dashboard.affiliate.earnings')}
        </span>
        <div className={'flex flex-col px-6 py-4'}>
          <StatisticWrapper title={t('dashboard.affiliate.today')} isFirst>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.today,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.today,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.last_7_days')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.last7Days,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.last7Days,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.last_30_days')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.last30Days.sums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.last30Days.sums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.paid')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.paidSums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.paidSums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper title={t('dashboard.affiliate.pending')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.pendingSums,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.pendingSums,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
          <StatisticWrapper isLast title={t('dashboard.affiliate.lifetime_earnings')}>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic =>
                selectedCurrency
                  ? getAffiliateSum(
                      affiliateStatistic.total,
                      selectedCurrency,
                      user?.dashboardLocale,
                    )
                  : getAffiliateTotalSum(
                      affiliateStatistic.total,
                      t,
                      mainCurrency,
                      user?.dashboardLocale,
                    )
              }
            </StatisticContent>
          </StatisticWrapper>
        </div>
      </div>
      <div
        className={
          'bg-background-secondary flex w-full flex-col gap-3 overflow-x-hidden rounded-lg px-6 py-4'
        }
      >
        {isDashboardStatistic(isDashboard, affiliateStatistic) ? (
          <div className={'flex w-full justify-between gap-1.5'}>
            <span className={'text-text-primary whitespace-nowrap'}>
              {t('dashboard.affiliate_dashboard.your_affiliate_id')}
            </span>
            <div className={'flex w-full justify-end gap-1.5 truncate'}>
              <StatisticContent affiliateStatistic={affiliateStatistic}>
                {affiliateStatistic => affiliateStatistic.user.affiliateAccount.affiliateId}
              </StatisticContent>
              {affiliateStatistic && (
                <Tooltip mode="hover" label={t('global.copy')}>
                  <button
                    onClick={async () => {
                      await copyTextToClipboard(
                        affiliateStatistic.user.affiliateAccount.affiliateId,
                        tUi,
                      )
                    }}
                    className="group outline-hidden"
                  >
                    <CopyIcon className="group-focus-visible:fill-brand-primary hover:fill-brand-primary h-[16px] w-[16px] cursor-pointer" />
                  </button>
                </Tooltip>
              )}
              <Popover
                label={t('dashboard.affiliate_dashboard.affiliate_id_tooltip-2')}
                popoverClassName={'w-[250px] sm:w-[450px]'}
              >
                {show => (
                  <QuestionMarkIcon
                    className={`${
                      show ? 'fill-brand-primary' : 'fill-fill-primary'
                    } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                  />
                )}
              </Popover>
            </div>
          </div>
        ) : (
          <div className={'flex w-full justify-between gap-1.5'}>
            <span className={'text-text-primary whitespace-nowrap'}>
              {t('dashboard.affiliate.affiliate_id')}
            </span>
            <StatisticContent affiliateStatistic={affiliateStatistic}>
              {affiliateStatistic => affiliateStatistic.contact.affiliateAccount.affiliateId}
            </StatisticContent>
          </div>
        )}
        <div className={'flex w-full justify-between gap-1.5'}>
          <span className={'text-text-primary'}>{t('dashboard.affiliate.total_leads')}</span>
          <StatisticContent affiliateStatistic={affiliateStatistic}>
            {affiliateStatistic => affiliateStatistic.totalLeads}
          </StatisticContent>
        </div>
      </div>
    </div>
  )
}

const StatisticWrapper = ({
  title,
  isLast,
  isFirst,
  children,
}: {
  title: string
  isLast?: boolean
  isFirst?: boolean
  children: JSX.Element
}) => (
  <div
    className={`flex justify-between gap-1.5 ${isFirst && 'pt-0'} ${
      isLast ? 'pt-5' : 'border-b-text-secondary/30 border-b py-5'
    } items-center`}
  >
    <span className={'text-text-primary'}>{title}</span>
    {children}
  </div>
)

const isDashboardStatistic = (
  isDashboard: boolean,
  stats?: AffiliateStatisticCommonInterface,
): stats is AffiliateDashboardStatisticInterface => isDashboard

const StatisticContent = <T,>({
  affiliateStatistic,
  children,
}: {
  affiliateStatistic?: T
  children: (affiliateStatistic: T) => JSX.Element | string | number
}) =>
  affiliateStatistic ? (
    <span className={'text-text-primary truncate font-bold'}>{children(affiliateStatistic)}</span>
  ) : (
    <span className={'bg-skeleton-primary animate-pulse rounded-lg px-5 text-transparent'}>.</span>
  )

export default AffiliateStatistic
